import {
	ChatMessage,
	ChatMessageType,
	ConversationState
} from './WSCommunicationTypes';
import {
	Backdrop,
	Button,
	CircularProgress,
	Grow,
	Paper,
	PaperProps,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import ChatMessageDisplay from './ChatMessageDisplay';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { Send, Stop } from '@mui/icons-material';

export type ChatProps = {
	chatStatus: ConversationState;
	chatMessages: ChatMessage[];
	onSendUserMessage?: (msg: string) => boolean;
	onPauseConversation?: () => void;
};

function isUserMessage(msg: ChatMessage): boolean {
	return (
		msg.t === ChatMessageType.UserMessage ||
		msg.t === ChatMessageType.UserError
	);
}

export default function Chat({
	chatStatus,
	chatMessages,
	onSendUserMessage,
	onPauseConversation,
	sx,
	...props
}: ChatProps & PaperProps) {
	const [newTextMessage, setNewTextMessage] = useState('');
	const [expandedMessage, setExpandedMessage] = useState<
		ChatMessage | undefined
	>(undefined);
	const messagesContainerRef = useRef<HTMLDivElement | null>(null);

	const sendMessage = () => {
		if (newTextMessage === '' || onSendUserMessage === undefined) {
			return;
		}
		const clearMessage = onSendUserMessage(newTextMessage);
		if (clearMessage) {
			setNewTextMessage('');
		}
	};

	useEffect(() => {
		const messagesContainer = messagesContainerRef.current;

		if (!messagesContainer) {
			return;
		}

		messagesContainer.scrollTo({
			top: messagesContainer.scrollHeight,
			behavior: 'smooth'
		});
	}, [chatMessages.length]);

	return (
		<Paper
			elevation={2}
			sx={{
				width: '100%',
				maxWidth: '100%',
				minWidth: 0,
				boxSizing: 'border-box',
				display: 'flex',
				flexDirection: 'column',
				maxHeight: 750,
				position: 'relative',
				overflow: 'hidden',
				...sx
			}}
			{...props}
		>
			<Stack
				ref={messagesContainerRef}
				sx={{
					flex: 1,
					flexGrow: 1,
					flexShrink: 1,
					minWidth: 0,
					boxSizing: 'border-box',
					overflowY: 'auto',
					minHeight: 0,
					p: 2,
					gap: 1
				}}
			>
				<TransitionGroup
					component={Stack}
					sx={{ gap: 1, width: '100%', minWidth: 0 }}
				>
					{chatMessages.map((msg, i) => (
						<Grow key={i} timeout={250}>
							<div
								style={{
									width: 'fit-content',
									maxWidth: '90%',
									minWidth: 0,
									display: 'flex',
									alignSelf: isUserMessage(msg)
										? 'flex-end'
										: 'flex-start'
								}}
							>
								<ChatMessageDisplay
									msg={msg}
									onClick={setExpandedMessage}
								/>
							</div>
						</Grow>
					))}
				</TransitionGroup>
				{chatStatus === ConversationState.Ongoing && (
					<Stack gap={1} direction='row' sx={{ p: 2 }}>
						<Typography sx={{ alignSelf: 'center' }}>
							{'Denke nach…'}
						</Typography>
						<CircularProgress size={20} />
					</Stack>
				)}
				{chatStatus === ConversationState.Disconnected && (
					<Typography
						sx={{ alignSelf: 'center', fontStyle: 'italic' }}
					>
						{'Websocket disconnected'}
					</Typography>
				)}
				<Backdrop
					open={!!expandedMessage}
					sx={{
						position: 'absolute',
						zIndex: 10,
						inset: 0,
						backgroundColor: 'rgba(0,0,0,0.2)'
					}}
					onClick={() => setExpandedMessage(undefined)}
				>
					<Grow in={!!expandedMessage} timeout={250}>
						<div>
							{expandedMessage ? (
								<ChatMessageDisplay
									msg={expandedMessage}
									onClick={() =>
										setExpandedMessage(undefined)
									}
									expand
								/>
							) : (
								<div />
							)}
						</div>
					</Grow>
				</Backdrop>
			</Stack>
			{(onSendUserMessage !== undefined ||
				onPauseConversation !== undefined) && (
				<Stack
					gap={2}
					direction='row'
					sx={{
						p: 2,
						width: '100%',
						minWidth: 0,
						boxSizing: 'border-box'
					}}
				>
					{onSendUserMessage !== undefined && (
						<>
							<TextField
								type='text'
								placeholder='Schreibe eine Nachricht'
								value={newTextMessage}
								onChange={(e) =>
									setNewTextMessage(e.target.value)
								}
								onKeyDown={(e) => {
									if (e.key !== 'Enter') {
										return;
									}
									e.preventDefault();
									e.stopPropagation();
									if (e.nativeEvent.isComposing) {
										return;
									}
									sendMessage();
								}}
								fullWidth
								sx={{ minWidth: 0 }}
							/>
							<Button
								variant={
									newTextMessage === ''
										? 'outlined'
										: 'contained'
								}
								endIcon={<Send />}
								disabled={
									chatStatus ===
										ConversationState.NotStarted ||
									chatStatus === ConversationState.Finished ||
									chatStatus === ConversationState.Other ||
									chatStatus ===
										ConversationState.Disconnected
								}
								onClick={sendMessage}
							>
								{'Senden'}
							</Button>
						</>
					)}
					{onPauseConversation !== undefined && (
						<Button
							variant={
								newTextMessage === '' ? 'outlined' : 'contained'
							}
							disabled={chatStatus !== ConversationState.Ongoing}
							onClick={() => {
								if (onPauseConversation === undefined) {
									return;
								}
								onPauseConversation();
							}}
							endIcon={<Stop />}
						>
							{'Stop'}
						</Button>
					)}
				</Stack>
			)}
		</Paper>
	);
}
